import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { Calendar, Loader2, Phone, Car, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Appointment {
    appt_id: number;
    customer_name: string | null;
    customer_phone: string | null;
    customer_email: string | null;
    vehicle_year: number | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_vin: string | null;
    service_type: string | null;
    appt_date: string | null;
    appt_time: string | null;
    status: string;
    notes: string | null;
}

const STATUS_COLOR: Record<string, string> = {
    SCHEDULED:   "bg-blue-100 text-blue-700",
    CONFIRMED:   "bg-indigo-100 text-indigo-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    COMPLETED:   "bg-green-100 text-green-700",
    CANCELLED:   "bg-gray-100 text-gray-500",
};

export default function ServiceAppointmentsPage() {
    const [appts,   setAppts]   = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [date,    setDate]    = useState(new Date().toISOString().slice(0, 10));

    const load = (d: string) => {
        setLoading(true);
        axios.get(`${API_URL}/repair-shops/staff/appointments${d ? `?date=${d}` : ""}`, { withCredentials: true })
            .then((r) => setAppts(r.data.appointments ?? []))
            .catch(() => setAppts([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(date); }, [date]);

    const updateStatus = async (apptId: number, status: string) => {
        try {
            const appt = appts.find((a) => a.appt_id === apptId);
            if (!appt) return;
            await axios.patch(
                `${API_URL}/repair-shops/${0}/appointments/${apptId}`,
                { status },
                { withCredentials: true }
            );
            setAppts((prev) => prev.map((a) => a.appt_id === apptId ? { ...a, status } : a));
        } catch { /* ignore */ }
    };

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Appointments</h1>
                    <p className="text-muted-foreground text-sm mt-1">{appts.length} appointment{appts.length !== 1 ? "s" : ""} on {date}</p>
                </div>
            </div>

            <div className="flex items-center gap-4">
                <label className="text-sm font-medium">Date</label>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="rounded-lg border bg-background px-3 py-1.5 text-sm"
                />
                <button onClick={() => setDate(new Date().toISOString().slice(0, 10))} className="text-sm text-indigo-600 hover:underline">
                    Today
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : appts.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No appointments for this date</p>
                </Card>
            ) : (
                <div className="space-y-3">
                    {appts.map((a) => (
                        <Card key={a.appt_id} className="p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium">{a.customer_name ?? "Walk-In"}</p>
                                        <Badge className={`text-xs ${STATUS_COLOR[a.status] ?? "bg-gray-100 text-gray-600"} hover:${STATUS_COLOR[a.status]}`}>
                                            {a.status}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground font-semibold">
                                        {a.appt_time} · {a.service_type ?? "General Service"}
                                    </p>
                                    {(a.vehicle_year || a.vehicle_make) && (
                                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                                            <Car className="h-3.5 w-3.5" />
                                            {a.vehicle_year} {a.vehicle_make} {a.vehicle_model}
                                            {a.vehicle_vin && ` · ${a.vehicle_vin}`}
                                        </p>
                                    )}
                                    {a.customer_phone && (
                                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                                            <Phone className="h-3.5 w-3.5" />
                                            {a.customer_phone}
                                        </p>
                                    )}
                                    {a.notes && <p className="text-xs text-muted-foreground mt-1">{a.notes}</p>}
                                </div>
                                <div className="flex flex-col gap-2 min-w-[130px]">
                                    {a.status === "SCHEDULED" && (
                                        <Button size="sm" variant="outline" onClick={() => updateStatus(a.appt_id, "CONFIRMED")}>
                                            Confirm
                                        </Button>
                                    )}
                                    {(a.status === "SCHEDULED" || a.status === "CONFIRMED") && (
                                        <Link to={`/service/repair-orders/new?apptId=${a.appt_id}&customerId=${a.customer_name ?? ""}&vin=${a.vehicle_vin ?? ""}&desc=${[a.vehicle_year, a.vehicle_make, a.vehicle_model].filter(Boolean).join(" ")}`}>
                                            <Button size="sm" className="w-full bg-indigo-600 text-white hover:bg-indigo-700">
                                                <Plus className="h-3 w-3 mr-1" />Start RO
                                            </Button>
                                        </Link>
                                    )}
                                    {a.status !== "CANCELLED" && a.status !== "COMPLETED" && (
                                        <Button size="sm" variant="ghost" className="text-red-500 text-xs" onClick={() => updateStatus(a.appt_id, "CANCELLED")}>
                                            Cancel
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
